import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid';

import type { ExpectedDiscrepancy } from '../../api/audit-config-queries';
import PreviewDialog from './PreviewDialog';
import { isExpiryActive } from '../../utils/expiry';
import { expectedGapKey, indexMatchCounts } from '../../utils/clientKey';
import { useConfigSectionEditor } from './useConfigSectionEditor';
import { usePendingGridEdits } from './usePendingGridEdits';
import {
  SaveSuccessSnackbar,
  SectionAlerts,
  SectionProvenance,
  SectionTitle,
  UnsavedChangesGuard,
} from './SectionEditorChrome';

interface GapRow {
  id: number;
  clientName: string;
  type: string;
  reason: string;
  expiresAt: string;
}

const ISSUE_TYPE_OPTIONS = [
  'missing_huntress',
  'missing_threatlocker',
  'missing_msp360',
  'orphaned_device',
  'av_unhealthy',
  'av_not_protected',
] as const;

/** Expired per the shared rule: date-only values last through end of day, America/Chicago. */
function isExpired(expiresAt: string): boolean {
  if (!expiresAt) return false;
  return !isExpiryActive(expiresAt);
}

function discrepanciesToRows(gaps: ExpectedDiscrepancy[]): GapRow[] {
  return gaps.map((gap, i) => ({
    id: i,
    clientName: gap.clientName,
    type: gap.type,
    reason: gap.reason,
    expiresAt: gap.expiresAt ?? '',
  }));
}

function rowsToDiscrepancies(rows: GapRow[]): ExpectedDiscrepancy[] {
  return rows.map((row) => ({
    clientName: row.clientName.trim(),
    type: row.type,
    reason: row.reason.trim(),
    ...(row.expiresAt ? { expiresAt: row.expiresAt } : {}),
  }));
}

/**
 * Custom single-select edit cell for the issue type column.
 */
function IssueTypeEditCell(params: GridRenderEditCellParams<GapRow, string>) {
  const { id, field, value } = params;
  const apiRef = useGridApiContext();

  function handleChange(newValue: string) {
    void apiRef.current.setEditCellValue({ id, field, value: newValue });
  }

  return (
    <Select
      value={value ?? ''}
      onChange={(e) => handleChange(e.target.value)}
      size="small"
      fullWidth
      sx={{ fontSize: '0.875rem' }}
    >
      {ISSUE_TYPE_OPTIONS.map((t) => (
        <MenuItem key={t} value={t}>
          {t}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Custom date input edit cell for the expiresAt column.
 * Uses a native <input type="date"> — @mui/x-date-pickers is not in dependencies.
 */
function DateEditCell(params: GridRenderEditCellParams<GapRow, string>) {
  const { id, field, value } = params;
  const apiRef = useGridApiContext();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    void apiRef.current.setEditCellValue({ id, field, value: e.target.value });
  }

  // Convert ISO string to YYYY-MM-DD for native date input
  const dateValue = value ? value.slice(0, 10) : '';

  return (
    <TextField
      type="date"
      value={dateValue}
      onChange={handleChange}
      size="small"
      fullWidth
      slotProps={{ htmlInput: { style: { fontSize: '0.875rem' } } }}
    />
  );
}

/**
 * Expected Gaps settings section.
 *
 * Editable DataGrid for expected discrepancies — known audit gaps that should not
 * trigger alerts. Supports per-row expiry dates. Expired rows are shown
 * struck-through; rows that matched no discrepancy in the latest audit get a
 * "Stale" badge (the gap they document has been fixed).
 *
 * Preview-before-apply via PreviewDialog; state and save flow via useConfigSectionEditor.
 */
export default function ExpectedGapsSection() {
  const editor = useConfigSectionEditor<GapRow, ExpectedDiscrepancy[]>({
    section: 'expected-discrepancies',
    label: 'Expected Gaps',
    toRows: discrepanciesToRows,
    fromRows: rowsToDiscrepancies,
  });
  const { rows, setRows } = editor;
  const pendingEdits = usePendingGridEdits<GapRow>();

  // Latest audit's match counts per gap (canonical client + type); 0 = stale
  const status = editor.data?.expectedDiscrepancyStatus ?? null;
  const aliases = editor.data?.clientAliases.value ?? {};
  const matchCounts = indexMatchCounts(status?.entries ?? [], aliases);

  function handleAddRow() {
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 0;
    setRows((prev) => [
      ...prev,
      { id: newId, clientName: '', type: 'missing_huntress', reason: '', expiresAt: '' },
    ]);
  }

  function handleDeleteRow(id: number) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  function handleProcessRowUpdate(newRow: GapRow): GapRow {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
    return newRow;
  }

  const columns: GridColDef<GapRow>[] = [
    {
      field: 'clientName',
      headerName: 'Client',
      flex: 1,
      editable: true,
    },
    {
      field: 'type',
      headerName: 'Issue Type',
      flex: 1.2,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams<GapRow, string>) => (
        <IssueTypeEditCell {...params} />
      ),
      renderCell: (params: GridRenderCellParams<GapRow, string>) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', py: 1 }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1.5,
      editable: true,
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 160,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams<GapRow, string>) => (
        <DateEditCell {...params} />
      ),
      renderCell: (params: GridRenderCellParams<GapRow, string>) => {
        if (!params.value) return null;
        const expired = isExpired(params.value);
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8rem',
              ...(expired ? { color: 'text.disabled', textDecoration: 'line-through' } : {}),
            }}
          >
            {params.value.slice(0, 10)}
          </Typography>
        );
      },
    },
    {
      field: '_status',
      headerName: 'Last audit',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<GapRow>) => {
        const count = matchCounts.get(expectedGapKey(params.row, aliases));
        if (count === undefined) return null;
        if (count === 0) {
          return (
            <Tooltip
              title={`Matched no discrepancies in the audit run of ${status ? new Date(status.evaluatedAt).toLocaleString() : 'the latest run'} — the gap may be fixed. Consider removing this entry.`}
            >
              <Chip label="Stale" size="small" color="warning" />
            </Tooltip>
          );
        }
        return (
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }} color="text.secondary">
            {count} suppressed
          </Typography>
        );
      },
    },
    {
      field: '_actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<GapRow>) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteRow(params.row.id)}
          aria-label="Delete row"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (editor.isLoading && !editor.data) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={140} />
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle title="Expected Gaps" isDirty={editor.isDirty} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Suppresses audit alerts for known acceptable discrepancies. Expired entries are shown
        struck-through and should be renewed or removed. Entries marked Stale matched nothing in the
        latest audit.
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontStyle: 'italic', fontSize: '0.8rem' }}
      >
        Example: Client &quot;Acme Corp&quot;, Type &quot;missing_threatlocker&quot;, Reason
        &quot;Pending TL deployment scheduled for Q2&quot;, Expires &quot;2026-06-30&quot;
      </Typography>

      <SectionAlerts
        loadError={editor.loadError}
        hasConflict={editor.hasConflict}
        validationError={editor.validationError}
        onDiscard={editor.discardChanges}
      />

      <DataGrid
        rows={rows}
        columns={columns}
        processRowUpdate={handleProcessRowUpdate}
        {...pendingEdits.gridProps}
        disableRowSelectionOnClick
        hideFooter={rows.length <= 100}
        autoHeight
        getRowClassName={(params) =>
          isExpired((params.row as GapRow).expiresAt) ? 'row-expired' : ''
        }
        sx={{
          mb: 1.5,
          '& .row-expired': {
            opacity: 0.5,
            '& .MuiDataGrid-cell': {
              textDecoration: 'line-through',
            },
          },
        }}
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow} variant="outlined">
          Add Gap
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => editor.openPreview(pendingEdits.withPendingEdits(rows))}
          disabled={
            editor.preview.isPending ||
            (!editor.isDirty && !pendingEdits.editing) ||
            !!editor.validationError
          }
        >
          {editor.preview.isPending ? 'Computing...' : 'Preview Changes'}
        </Button>
        {editor.isDirty && (
          <Button size="small" color="inherit" onClick={editor.discardChanges}>
            Discard
          </Button>
        )}
      </Box>

      <SectionProvenance record={editor.record} />

      <PreviewDialog
        open={editor.preview.open}
        preview={editor.preview.data}
        previewLoading={editor.preview.isPending}
        error={editor.preview.error}
        applying={editor.applying}
        applyError={editor.applyError}
        writeDisabledReason={editor.writeDisabledReason}
        onApply={() => void editor.apply()}
        onCancel={editor.closePreview}
      />
      <SaveSuccessSnackbar message={editor.successMessage} onClose={editor.clearSuccess} />
      <UnsavedChangesGuard when={editor.isDirty} />
    </Box>
  );
}
